import { ContextMenuHandler } from "@asup/context-menu";
import React, { useMemo } from "react";
import styles from "../aic.module.css";
import { MosaicTrafficLightClass } from "../traffic-lights/MosaicTrafficLightClass";
import { defaultEditorDefns } from "../utility";
import { classMerge } from "../utility/classMerge";
import { AitCellWindow } from "./AitCellWindow";
import { clickCell } from "./AitHeaderCell";
import { AitCellData } from "./functions/AitCellData";
import { getBaseId } from "./functions/getBaseId";
import {
  AitCellType,
  AitLocationBodyCell,
  SHOW_OPTIONS_WINDOW,
  UPDATE_TABLE_CELL,
} from "./functions/interface";
import { trGetCellFromLocation } from "./functions/trGetCellFromLocation";
import { useTableContext } from "./TableContextProvider";

interface AitCellProps extends React.ComponentProps<"td"> {
  location: AitLocationBodyCell;
}

/**
 * Render a single table cell.
 *
 * The component resolves the cell data from the table state, computes
 * styles and renders the editor component. It also exposes a small options
 * menu and the cell window for inline actions.
 *
 * @template T - Cell payload type
 * @param props - Component props
 * @param props.location - Location identifying this cell within the table
 */
export const AitCell = <T,>({ location, ...rest }: AitCellProps) => {
  const { state, dispatch } = useTableContext<T>();
  const Editor = state?.editorProps.Editor;
  const thisCell: AitCellData<T> | undefined = state?.processedTableData
    ? trGetCellFromLocation(state.processedTableData, location)
    : undefined;
  const id = getBaseId(location, state?.id);
  const displayText: T =
    thisCell?.replacedText ??
    thisCell?.text ??
    state?.editorProps.blankT ??
    (defaultEditorDefns.blankT as T);

  // Static options/variables
  const currentReadOnly = useMemo<boolean>(
    () => (!state || !thisCell ? true : !state.editable || location.isRepeat),
    [location.isRepeat, state, thisCell],
  );

  // Update cell style when options change
  const cellStyle = useMemo<React.CSSProperties>(() => {
    return !state || !thisCell
      ? {}
      : {
          overflow: "visible",
          width: `${
            state.colWidthMod *
            (thisCell.colWidth ??
              // istanbul ignore next
              state.defaultCellWidth)
          }px`,
          paddingLeft:
            location.cellType === AitCellType.rowHeader && thisCell.textIndents !== undefined
              ? `${thisCell.textIndents}rem`
              : undefined,
          borderLeft: state.showCellBorders ? "1px dashed burlywood" : "",
          borderBottom: state.showCellBorders ? "1px dashed burlywood" : "",
          borderRight:
            state.showCellBorders &&
            location.column ===
              (state.processedTableData?.rowHeaderColumns ??
                // istanbul ignore next
                0) -
                1
              ? "1px solid burlywood"
              : state.showCellBorders
                ? "1px dashed burlywood"
                : "",
          borderTop:
            state.showCellBorders &&
            location.row === 0 &&
            location.rowGroup !== undefined &&
            location.rowGroup > 0
              ? "1px solid burlywood"
              : state.showCellBorders
                ? "1px dashed burlywood"
                : "",
        };
  }, [location.cellType, location.column, location.row, location.rowGroup, state, thisCell]);

  // Traffic light
  const cellTrafficLight =
    !state?.noTrafficLights &&
    !location.isRepeat &&
    thisCell &&
    !["BlankNotEvaluated", "GreenOk"].includes(thisCell.coreTrafficLight)
      ? new MosaicTrafficLightClass(thisCell!.coreTrafficLight, "-4px", "-8px", "absolute").element
      : null;

  // Render element
  return !state || !id || !state.processedTableData || !Editor || !thisCell ? null : (
    <td
      {...rest}
      id={id}
      className={classMerge([
        styles.aitCell,
        location.cellType === AitCellType.rowHeader ? styles.aitRowHeaderCell : styles.aitBodyCell,
        currentReadOnly ? styles.aitReadonlyCell : "",
      ])}
      style={cellStyle}
      role="cell"
      onClick={clickCell}
    >
      <div
        style={{
          width: `calc(${cellStyle.width} - ${
            thisCell.textIndents ??
            // istanbul ignore next
            0
          }rem)`,
          height: "100%",
        }}
      >
        {cellTrafficLight}
        <ContextMenuHandler
          className={styles.aitAieHolder}
          menuItems={[
            {
              label: "Show cell options",
              action: () => {
                const focusDummy = document.createElement("input");
                document.body.appendChild(focusDummy);
                focusDummy.focus();
                focusDummy.remove();
                dispatch({ operation: SHOW_OPTIONS_WINDOW, location });
              },
            },
          ]}
        >
          <Editor
            id={`${id}-editor`}
            decimalAlignPercent={state.processedTableData.decimalAlignPercent}
            editable={!currentReadOnly}
            preReplacedText={thisCell.text}
            setValue={(ret) => {
              dispatch({ operation: UPDATE_TABLE_CELL, location, cellData: { text: ret } });
            }}
            className={styles.editorInput}
            textAlignment={
              !thisCell.justifyText || thisCell.justifyText === "default"
                ? location.column <
                  (state.processedTableData.rowHeaderColumns ??
                    // istanbul ignore next
                    0)
                  ? "left"
                  : "center"
                : thisCell.justifyText
            }
            value={displayText}
            commentConfig={{
              id,
              activeContext: "TlfItem",
              label: "New cell comment",
              iconPosition: { top: "-2px", left: "-2px" },
              noIcon: location.isRepeat,
            }}
          />
        </ContextMenuHandler>
      </div>
      <AitCellWindow location={location} />
    </td>
  );
};

AitCell.displayName = "AitCell";
